import { AdapterType, ProviderConfig } from "./types";

// A known regional variant of a preset's endpoint — currently only Kimi and Qwen have more than
// one (see PROVIDER_PRESETS below for why). `key` is a stable per-preset identifier (just "intl"/
// "cn" today) used to match the Region <select> in providerForm.ts back to one of these, not
// shown to the user (`label` is). The first entry in a preset's `regions` array is that preset's
// default, matched by `ProviderPreset.endpoint`/`apiKeyUrl` below.
export interface ProviderPresetRegion {
  key: string;
  label: string;
  endpoint: string;
  apiKeyUrl?: string;
}

export interface ProviderPreset {
  key: string;
  label: string;
  // The default region's endpoint for presets with `regions` (must equal `regions[0].endpoint`);
  // the only endpoint for presets without one.
  endpoint: string;
  adapterType: AdapterType;
  // Where to actually generate/manage a key for this provider — shown as a "Get an API key"
  // link next to the API key field in providerForm.ts. Verified against each provider's own
  // current console 2026-08-09 (not guessed/pattern-matched) — these pages move without notice
  // (DashScope's own console was retired 2026-08-01, days before this was written, in favor of
  // the Bailian console at the qwen entry below), so re-verify against the provider's own site
  // if a user ever reports one as dead rather than assuming the URL below is still current.
  // Omitted for "custom" — there's no known console for an arbitrary/unlisted endpoint. Same
  // "default region" rule as `endpoint` above when `regions` is present.
  apiKeyUrl?: string;
  // Present only for providers that issue separate, non-interchangeable accounts/API keys per
  // region (see the comment on the kimi/qwen entries below) — providerForm.ts shows a small
  // "Region" dropdown only when this is set, defaulting to `regions[0]`. Absent for every other
  // preset, including "Custom / other".
  regions?: ProviderPresetRegion[];
}

// Known endpoints for the providers named in the PRD (Section 6.1: "pre-filled/known for common providers, editable").
// No default/example model is stored here — model names change too often to hardcode reliably;
// the form fetches the live list from the provider instead (see providerForm.ts). Per-model
// context-size defaults live in storage.ts's getDefaultMaxContextTokens (live API data where
// available, else core/modelContextSizes.ts's name-pattern table) — not here, since a single
// per-preset number can't represent a provider whose own model lineup varies widely.
// Alphabetical by label, with "Custom / other" always last as the fallback.
export const PROVIDER_PRESETS: ProviderPreset[] = [
  {
    key: "openai",
    label: "ChatGPT (OpenAI)",
    endpoint: "https://api.openai.com/v1",
    adapterType: "openai-compatible",
    apiKeyUrl: "https://platform.openai.com/api-keys",
  },
  {
    key: "claude",
    label: "Claude (Anthropic)",
    endpoint: "https://api.anthropic.com/v1",
    adapterType: "anthropic",
    apiKeyUrl: "https://console.anthropic.com/settings/keys",
  },
  {
    key: "deepseek",
    label: "DeepSeek",
    endpoint: "https://api.deepseek.com",
    adapterType: "openai-compatible",
    apiKeyUrl: "https://platform.deepseek.com/api_keys",
  },
  {
    key: "gemini",
    label: "Gemini",
    endpoint: "https://generativelanguage.googleapis.com/v1beta",
    adapterType: "gemini",
    apiKeyUrl: "https://aistudio.google.com/app/apikey",
  },
  // Kimi and Qwen each carry a `regions` list (2026-08-12) — one preset entry each in this list
  // (not two, as an earlier same-day version tried), but with a "Region" dropdown in
  // providerForm.ts to switch between them. Both providers issue separate, non-interchangeable
  // accounts/API keys per region, confirmed against each provider's own current docs: a
  // China-region key against the international endpoint (or vice versa) fails with a plain 401,
  // not a clearer wrong-endpoint error — so this genuinely needs to be choosable, not just a
  // single hardcoded default. **Defaults to International** (`regions[0]`, also mirrored in
  // `endpoint`/`apiKeyUrl` above) per explicit request — was briefly the reverse for Qwen
  // (defaulted to China) before this regions-array version replaced the earlier two-separate-
  // presets attempt. Auto-detecting which region a user needs (IP/OS/Word locale) was considered
  // and rejected — physical location doesn't reliably predict which account someone actually
  // registered (an overseas author could hold a mainland account, or vice versa), so guessing
  // risks silently producing the same confusing 401 this dropdown exists to prevent. The endpoint
  // field itself still accepts free typing regardless of which region is selected, and "Custom /
  // other" remains the fallback for a genuinely different/self-hosted endpoint either might use.
  {
    key: "kimi",
    label: "Kimi (Moonshot)",
    endpoint: "https://api.moonshot.ai/v1",
    adapterType: "openai-compatible",
    apiKeyUrl: "https://platform.moonshot.ai/console/api-keys",
    regions: [
      {
        key: "intl",
        label: "International",
        endpoint: "https://api.moonshot.ai/v1",
        apiKeyUrl: "https://platform.moonshot.ai/console/api-keys",
      },
      {
        key: "cn",
        label: "China",
        endpoint: "https://api.moonshot.cn/v1",
        apiKeyUrl: "https://platform.moonshot.cn/console/api-keys",
      },
    ],
  },
  {
    key: "qwen",
    label: "Qwen (DashScope)",
    // dashscope-intl.aliyuncs.com (Singapore) — a genuinely different endpoint from the China
    // region below, not just a regional alias; a China-account key doesn't work here either.
    endpoint: "https://dashscope-intl.aliyuncs.com/compatible-mode/v1",
    adapterType: "openai-compatible",
    // International Model Studio console — a single console with an in-app region selector
    // (Singapore/Tokyo/Hong Kong/Frankfurt), unlike the China region's own deep-linked
    // ?tab=model#/api-key fragment (that fragment is Beijing-console-specific, confirmed not to
    // carry over here — verified 2026-08-12 against alibabacloud.com's own docs). Linking to the
    // console root rather than guessing an equivalent international fragment.
    apiKeyUrl: "https://bailian.console.alibabacloud.com",
    regions: [
      {
        key: "intl",
        label: "International",
        endpoint: "https://dashscope-intl.aliyuncs.com/compatible-mode/v1",
        apiKeyUrl: "https://bailian.console.alibabacloud.com",
      },
      {
        key: "cn",
        label: "China",
        endpoint: "https://dashscope.aliyuncs.com/compatible-mode/v1",
        // Not dashscope.console.aliyun.com — that console was retired 2026-08-01 in favor of the
        // Bailian (百炼) console, which is now where DashScope API keys are actually managed
        // despite the endpoint itself still being under the dashscope.aliyuncs.com domain.
        apiKeyUrl: "https://bailian.console.aliyun.com/?tab=model#/api-key",
      },
    ],
  },
  {
    key: "custom",
    label: "Custom / other",
    endpoint: "",
    adapterType: "openai-compatible",
  },
];

// Strips a preset label's parenthetical clarifier (e.g. "ChatGPT (OpenAI)" -> "ChatGPT") — that
// suffix exists to disambiguate presets in the picker list, not as part of the provider's actual
// name. Also used as the Display Name field's placeholder in providerForm.ts.
export function presetShortLabel(label: string): string {
  return label.replace(/\s*\([^)]*\)\s*$/, "").trim();
}

// Matches a saved (or in-progress) endpoint+adapterType back to whichever preset produced it —
// checking each preset's own default endpoint first, then (2026-08-12, for Kimi/Qwen) each of
// its `regions`, since a provider saved against the China region's endpoint no longer matches
// the preset's own top-level `endpoint` (that's the International default now). Returns the
// matched region too, if any, so callers (providerForm.ts's edit-form load, getPresetProviderName
// below) can tell a plain preset match from a specific-region one without redoing this search.
export function findPresetMatch(
  endpoint: string,
  adapterType: AdapterType
): { preset: ProviderPreset; region?: ProviderPresetRegion } | undefined {
  // Strips a trailing slash before comparing, same normalization the adapters themselves apply
  // (adapters/types.ts's normalizeEndpoint) before building a request URL — duplicated as a
  // one-line regex here rather than imported, to keep this data module out of the adapters
  // layer. Without this, a saved endpoint with a trailing slash (e.g. pasted with one, or typed
  // by hand) still sends real requests fine but silently fails to match its preset here.
  const normalized = endpoint.replace(/\/+$/, "");
  for (const preset of PROVIDER_PRESETS) {
    if (preset.key === "custom" || preset.adapterType !== adapterType) continue;
    if (preset.endpoint === normalized) return { preset };
    const region = preset.regions?.find((r) => r.endpoint === normalized);
    if (region) return { preset, region };
  }
  return undefined;
}

// The provider's real name (e.g. "Gemini", "DeepSeek") as opposed to its user-editable Display
// Name (ProviderConfig.name — freeform, e.g. "Gemini — Creative" for a second differently-tuned
// entry on the same provider). Falls back to the Display Name for "Custom / other" or any
// endpoint that doesn't match a known preset/region, since there's no real name to recover there.
export function getPresetProviderName(
  provider: Pick<ProviderConfig, "endpoint" | "adapterType" | "name">
): string {
  const match = findPresetMatch(provider.endpoint, provider.adapterType);
  return match ? presetShortLabel(match.preset.label) : provider.name;
}
